import { Tooltip } from '@chakra-ui/react';
import classNames from 'classnames';
import { extent, scaleBand, scaleLinear } from 'd3';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  useChartTheme,
  useResizeObserver,
  VOLUME_NEGATIVE_COLOR,
  VOLUME_POSITIVE_COLOR,
} from '../../utilities';
import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import axisLabelStyles from '../axis/axis-label.module.css';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './diverging-bar-chart.module.css';

export type DivergingBarLayout = { centerX: number };

const Dimensions = {
  BarLeftPadding: 4,
  paddingInner: 0.4,
  paddingOuter: 0.4,
};

const NEW_THEME_PADDING_INNER = 0.54;
const NEW_THEME_MAX_BAR_THICKNESS = 48;

export type DivergingBarData = {
  label: string;
  leftValue: number | null;
  rightValue: number | null;
  leftLabel: string;
  rightLabel: string;
  leftId?: string | null;
  rightId?: string | null;
};

export type DivergingBarChartProps = {
  data: DivergingBarData[];
  leftColor?: string;
  rightColor?: string;
  showBarLabels?: boolean;
  tooltipContent?: (
    data: DivergingBarData,
    side: 'left' | 'right'
  ) => JSX.Element;
  valueLabel?: (data: DivergingBarData, side: 'left' | 'right') => string;
  onLayout?: (layout: DivergingBarLayout) => void;
  footer?: (layout: DivergingBarLayout) => ReactNode;
  footerHeight?: number;
  /** When true (default), data is sorted by net difference (rightValue + leftValue) descending. Set to false when parent provides pre-sorted data. */
  sortData?: boolean;
  /** Optional labels shown above the left and right bar areas (e.g. entity names in dual-view comparison). */
  leftHeaderLabel?: string;
  rightHeaderLabel?: string;
  /** Reserve space at top so bar area aligns with another chart that has a header (e.g. dual-view). */
  topOffset?: number;
  /** When false, y-axis row labels are not rendered (e.g. second chart in dual-view). Default true. */
  showYAxisLabels?: boolean;
  /** Optional fill-opacity for bar rects (0–1). Lets callers visually de-emphasize a chart in a dual-view. */
  barOpacity?: number;
  /** Fraction of the band height to use for bar thickness (0–1). Defaults to 1. Use a smaller value to visually de-emphasize bars while keeping them opaque. */
  barHeightRatio?: number;
  /** Maximum fraction of the chart width that the y-axis row labels can consume (0–1). Defaults to 0.35. */
  maxLabelWidthRatio?: number;
  barCornerRadius?: number;
  centerGap?: number;
  barLabelPosition?: 'auto' | 'inside' | 'outside';
  barLabelColor?: string;
  labelReserveRatio?: number;
} & CommonPlotProps;

const DEFAULT_FOOTER_HEIGHT = 25;
/** Height of the optional header row (entity labels). Export for dual-view alignment. */
export const DIVERGING_BAR_HEADER_LABEL_HEIGHT = 20;

export const DivergingBarChart = ({
  className,
  data,
  loading = false,
  format = FormatType.PERCENTAGE,
  leftColor: leftColorProp,
  rightColor: rightColorProp,
  showBarLabels = false,
  tooltipContent,
  valueLabel,
  onLayout,
  footer,
  footerHeight = DEFAULT_FOOTER_HEIGHT,
  sortData = true,
  leftHeaderLabel,
  rightHeaderLabel,
  topOffset = 0,
  showYAxisLabels = true,
  barOpacity,
  barHeightRatio = 1,
  maxLabelWidthRatio = 0.35,
  barCornerRadius = 2,
  centerGap = 0,
  barLabelPosition = 'auto',
  barLabelColor,
  labelReserveRatio = 0.4,
}: DivergingBarChartProps) => {
  const { containerRef, width, height } = useResizeObserver();

  const { palette, isNewTheme } = useChartTheme();
  const leftColor =
    leftColorProp ??
    (isNewTheme ? VOLUME_NEGATIVE_COLOR : (palette[0] ?? '#5CA9E9'));
  const rightColor =
    rightColorProp ??
    (isNewTheme ? VOLUME_POSITIVE_COLOR : (palette[1] ?? '#70CA97'));

  const hasHeaderLabels =
    !!leftHeaderLabel?.trim() || !!rightHeaderLabel?.trim();
  const headerHeight = hasHeaderLabels ? DIVERGING_BAR_HEADER_LABEL_HEIGHT : 0;
  const contentHeight = footer ? Math.max(0, height - footerHeight) : height;
  const plotHeight = Math.max(0, contentHeight - headerHeight - topOffset);
  const labelWidth =
    !showYAxisLabels || data.length === 0
      ? 0
      : Math.min(
          getMaxTextWidth({ texts: data.map((d) => d.label) }),
          width * maxLabelWidthRatio
        );
  const margin = labelWidth + Dimensions.BarLeftPadding;
  const plotWidth = Math.max(0, width - labelWidth - Dimensions.BarLeftPadding);
  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;

  const centerX = margin + plotWidth / 2;
  useEffect(() => {
    onLayout?.({ centerX });
  }, [onLayout, centerX]);

  const sortedData = sortData
    ? [...data].sort((a, b) => {
        const aDiff = (a.rightValue ?? 0) + (a.leftValue ?? 0);
        const bDiff = (b.rightValue ?? 0) + (b.leftValue ?? 0);
        return bDiff - aDiff;
      })
    : [...data];

  const barCount = sortedData.length;
  const paddingInner = isNewTheme
    ? NEW_THEME_PADDING_INNER
    : Dimensions.paddingInner;
  const paddingOuter =
    isNewTheme && barCount > 0 && plotHeight > 0
      ? Math.max(
          Dimensions.paddingOuter,
          ((plotHeight * (1 - NEW_THEME_PADDING_INNER)) /
            NEW_THEME_MAX_BAR_THICKNESS -
            barCount +
            NEW_THEME_PADDING_INNER) /
            2
        )
      : Dimensions.paddingOuter;
  const yScale = scaleBand()
    .domain(sortedData.map((d) => d.label))
    .range([0, plotHeight])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter);

  const allValues = sortedData
    .flatMap((d) => [d.leftValue, d.rightValue])
    .filter((v): v is number => v !== null);
  const [minValue, maxValue] = extent(allValues);

  const formatValue = getFormatter(format);
  const labelPadding = 6;

  const labelReserve = (() => {
    if (!showBarLabels) return 0;
    const labelTexts = sortedData.flatMap((d) => {
      const texts: string[] = [];
      if (typeof d.leftValue === 'number') {
        texts.push(
          valueLabel ? valueLabel(d, 'left') : formatValue(d.leftValue)
        );
      }
      if (typeof d.rightValue === 'number') {
        texts.push(
          valueLabel ? valueLabel(d, 'right') : formatValue(d.rightValue)
        );
      }
      return texts;
    });
    if (labelTexts.length === 0) return 0;
    const maxLabelWidth = getMaxTextWidth({ texts: labelTexts, fontSize: 11 });
    const reserve = maxLabelWidth + labelPadding;
    return Math.min(reserve, plotWidth * labelReserveRatio);
  })();

  const valueScale = (() => {
    if (minValue === undefined || maxValue === undefined) return null;
    const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
    return scaleLinear(
      [-absMax, absMax],
      [labelReserve, Math.max(labelReserve, plotWidth - labelReserve)]
    );
  })();

  const renderBar = (item: DivergingBarData) => {
    const y = yScale(item.label);
    if (y === undefined || !valueScale) return null;

    const bandHeight = yScale.bandwidth();
    const barHeight = bandHeight * barHeightRatio;
    const barY = y + (bandHeight - barHeight) / 2;

    const centerX = valueScale(0);
    const leftValue = item.leftValue;
    const rightValue = item.rightValue;
    const hasLeft = typeof leftValue === 'number';
    const hasRight = typeof rightValue === 'number';

    const leftX = hasLeft ? valueScale(leftValue) : centerX;
    const rightX = hasRight ? valueScale(rightValue) : centerX;

    const leftBarWidth = hasLeft ? Math.abs(centerX - leftX) : 0;
    const rightBarWidth = hasRight ? Math.abs(rightX - centerX) : 0;

    const leftBarWidthForRect =
      hasLeft && (leftValue === 0 || leftBarWidth === 0)
        ? Math.max(1, leftBarWidth)
        : leftBarWidth;
    const rightBarWidthForRect =
      hasRight && (rightValue === 0 || rightBarWidth === 0)
        ? Math.max(1, rightBarWidth)
        : rightBarWidth;

    const leftBarRectX = (() => {
      if (!hasLeft) return centerX;
      if (leftValue === 0 || leftBarWidth === 0) {
        return centerX - leftBarWidthForRect;
      }
      return Math.min(leftX, centerX);
    })();

    const halfGap = hasLeft && hasRight ? centerGap / 2 : 0;
    const leftRectWidth =
      leftBarWidthForRect > halfGap
        ? leftBarWidthForRect - halfGap
        : leftBarWidthForRect;
    const rightRectX = centerX + halfGap;
    const rightRectWidth =
      rightBarWidthForRect > halfGap
        ? rightBarWidthForRect - halfGap
        : rightBarWidthForRect;

    const leftBarLabel = (() => {
      if (!hasLeft) return '';
      if (valueLabel) return valueLabel(item, 'left');
      return formatValue(leftValue);
    })();

    const rightBarLabel = (() => {
      if (!hasRight) return '';
      if (valueLabel) return valueLabel(item, 'right');
      return formatValue(rightValue);
    })();

    return (
      <g key={item.label}>
        {hasLeft && (
          <Tooltip
            hasArrow
            label={
              tooltipContent ? (
                tooltipContent(item, 'left')
              ) : (
                <div className={styles.tooltipContent}>
                  <div className={styles.tooltipTitle}>{item.label}</div>
                  <div>
                    {item.leftLabel}: {formatValue(leftValue ?? 0)}
                  </div>
                </div>
              )
            }
            padding="8px 12px"
            placement="top"
            borderRadius="4px"
            className={tooltipStyles.surface}
          >
            <rect
              x={leftBarRectX}
              y={barY}
              width={leftRectWidth}
              height={barHeight}
              rx={barCornerRadius}
              fill={leftColor}
              fillOpacity={barOpacity}
              className={styles.bar}
              data-testid="diverging-bar-chart-bar-left"
            />
          </Tooltip>
        )}
        {showBarLabels &&
          hasLeft &&
          (() => {
            const labelWidthPx = leftBarLabel.length * 6;
            const fitsInside = leftBarWidthForRect > labelWidthPx + 8;
            const placeInside =
              barLabelPosition === 'auto'
                ? fitsInside
                : barLabelPosition === 'inside';

            let x: number;
            let textAnchor: 'start' | 'end';
            let fill: string;

            if (placeInside) {
              x = Math.min(leftX, centerX) + labelPadding;
              textAnchor = 'start';
              fill = 'white';
            } else {
              x = Math.min(leftX, centerX) - labelPadding;
              textAnchor = 'end';
              fill = barLabelColor ?? leftColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={11}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {leftBarLabel}
              </text>
            );
          })()}
        {hasRight && (
          <Tooltip
            hasArrow
            label={
              tooltipContent ? (
                tooltipContent(item, 'right')
              ) : (
                <div className={styles.tooltipContent}>
                  <div className={styles.tooltipTitle}>{item.label}</div>
                  <div>
                    {item.rightLabel}: {formatValue(rightValue ?? 0)}
                  </div>
                </div>
              )
            }
            padding="8px 12px"
            placement="top"
            borderRadius="4px"
            className={tooltipStyles.surface}
          >
            <rect
              x={rightRectX}
              y={barY}
              width={rightRectWidth}
              height={barHeight}
              rx={barCornerRadius}
              fill={rightColor}
              fillOpacity={barOpacity}
              className={styles.bar}
              data-testid="diverging-bar-chart-bar-right"
            />
          </Tooltip>
        )}
        {showBarLabels &&
          hasRight &&
          (() => {
            const labelWidthPx = rightBarLabel.length * 6;
            const fitsInside = rightBarWidthForRect > labelWidthPx + 8;
            const placeInside =
              barLabelPosition === 'auto'
                ? fitsInside
                : barLabelPosition === 'inside';

            let x: number;
            let textAnchor: 'start' | 'end';
            let fill: string;

            if (placeInside) {
              x = Math.max(centerX, rightX) - labelPadding;
              textAnchor = 'end';
              fill = 'white';
            } else {
              x = Math.max(centerX, rightX) + labelPadding;
              textAnchor = 'start';
              fill = barLabelColor ?? rightColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={11}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {rightBarLabel}
              </text>
            );
          })()}
      </g>
    );
  };

  const renderAxisLabel = (label: string) => {
    if (!showYAxisLabels) return null;
    const y = yScale(label);
    if (y === undefined) return null;

    return (
      <AxisLabel
        key={label}
        y={y + yScale.bandwidth() / 2}
        width={labelWidth}
        fontSize={11}
        label={label}
        availableHeight={yScale.bandwidth()}
        testId="diverging-bar-chart-axis-label"
      />
    );
  };

  const layout: DivergingBarLayout = { centerX };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      style={
        footer || hasHeaderLabels || topOffset > 0
          ? {
              display: 'flex',
              flexDirection: 'column',
            }
          : undefined
      }
      data-testid={plotTestId(ReplotChartNames.DivergingBarChart)}
    >
      {topOffset > 0 && (
        <div
          style={{
            flexShrink: 0,
            height: topOffset,
            width: '100%',
          }}
        />
      )}
      {hasHeaderLabels && (
        <div
          className={styles.headerRow}
          style={{
            height: headerHeight,
            paddingLeft: margin,
          }}
        >
          <div className={styles.headerGrid}>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelLeft
              )}
              title={leftHeaderLabel ?? ''}
            >
              {leftHeaderLabel ?? ''}
            </span>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelRight
              )}
              title={rightHeaderLabel ?? ''}
            >
              {rightHeaderLabel ?? ''}
            </span>
          </div>
        </div>
      )}
      <PlotLoadWrapper loading={loading} noData={sortedData.length === 0}>
        <div
          style={{
            width: '100%',
            flex:
              footer || hasHeaderLabels || topOffset > 0
                ? '1 1 auto'
                : undefined,
            minHeight:
              footer || hasHeaderLabels || topOffset > 0 ? 0 : undefined,
          }}
        >
          <svg width="100%" height={plotHeight}>
            <g id="y-axis">
              {isPlotSizeValid && yScale.domain().map(renderAxisLabel)}
            </g>
            <g id="chart" transform={`translate(${margin}, 0)`}>
              {isPlotSizeValid && valueScale && sortedData.map(renderBar)}
            </g>
          </svg>
        </div>
      </PlotLoadWrapper>
      {footer && (
        <div
          style={{
            flexShrink: 0,
            height: footerHeight,
            width: '100%',
          }}
        >
          {footer(layout)}
        </div>
      )}
    </div>
  );
};
